import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Dialog, IconButton, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useThemeColors } from '../hooks/useThemeColors';

/** 中身の量に合わせた3段階。これ以外の幅は使わない */
const WIDTH = { sm: 420, md: 560, lg: 720 } as const;

interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  /** 見出しの左に置く絵。既存のイラストがあるものは画像、無いものはアイコン */
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  size?: keyof typeof WIDTH;
  /** 消す操作。線とアイコンが赤になる */
  danger?: boolean;
  /** 下の行。左端の説明（件数など） */
  footerNote?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * アプリ共通のモーダル。
 *
 * 見出しの下にアクセントの線を1本引くだけにしてある。色の面を敷くと、
 * 中に置くイラストやチップの色とぶつかるため。消す操作のときだけ線が赤になる。
 */
const AppDialog: React.FC<AppDialogProps> = ({
  open,
  onClose,
  icon,
  title,
  subtitle,
  size = 'sm',
  danger = false,
  footerNote,
  actions,
  children,
}) => {
  const tokens = useThemeColors();
  const accent = danger ? '#d4433d' : tokens.teal;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxWidth: WIDTH[size],
            borderRadius: '18px',
            boxShadow: '0 24px 60px rgba(16,20,40,.28)',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          pt: 2,
          pb: 1.75,
          borderBottom: `3px solid ${accent}`,
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '12px',
              background: danger ? 'rgba(212,67,61,.1)' : `${tokens.teal}1f`,
              '& img': { width: 32, height: 32, objectFit: 'contain' },
              '& svg': { fontSize: 22, color: accent },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 900, color: tokens.ink, lineHeight: 1.4 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11, color: tokens.muted, mt: 0.3, lineHeight: 1.6 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disableRipple
          sx={{
            color: tokens.faint,
            alignSelf: 'flex-start',
            '&:hover': { color: tokens.ink, background: 'transparent' },
          }}
        >
          <CloseIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, py: 2, maxHeight: '60vh', overflow: 'auto' }}>{children}</Box>

      {(actions || footerNote) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${tokens.line}`,
            background: tokens.paper,
          }}
        >
          {footerNote && (
            <Typography sx={{ fontSize: 11.5, color: tokens.muted }}>{footerNote}</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {actions}
        </Box>
      )}
    </Dialog>
  );
};

/**
 * モーダルの中の入力欄。
 *
 * ラベルは枠の外に出す（MUI の既定だと枠の中から浮き上がるが、入力後にラベルが
 * 小さくなって読みづらく、何を入れる欄か分からなくなるため）。
 * 枠の中のうすい文字は、必ず具体例を書く。
 */
export const DialogField: React.FC<{
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  children?: React.ReactNode;
}> = ({ label, placeholder, value, onChange, multiline, rows = 3, required, children }) => {
  const tokens = useThemeColors();
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        component="label"
        sx={{
          display: 'block',
          fontSize: 11,
          fontWeight: 800,
          color: tokens.muted,
          mb: 0.75,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: '#d4433d', ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>
      {children ?? (
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          fullWidth
          size="small"
          multiline={multiline}
          rows={multiline ? rows : undefined}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '11px', fontSize: 13 },
            '& .MuiOutlinedInput-input::placeholder': { color: tokens.faint, opacity: 1 },
          }}
        />
      )}
    </Box>
  );
};

export default AppDialog;
